import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium",
    "transition-[background-color,box-shadow,transform,color,border-color] duration-200 ease-out",
    "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/25",
    "disabled:pointer-events-none disabled:opacity-50",
    "active:translate-y-px",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
  ),
  {
    variants: {
      variant: {
        // Primary — brand black with subtle elevation on hover
        default:
          "bg-primary text-primary-foreground shadow-brand hover:bg-[hsl(20_9%_18%)] hover:shadow-brand-md hover:-translate-y-px",
        // Accent — brand orange for the highest-value CTAs
        accent:
          "bg-brand-orange text-white shadow-brand hover:bg-[hsl(28_65%_45%)] hover:shadow-brand-orange hover:-translate-y-px",
        // Destructive — brand brown
        destructive:
          "bg-destructive text-destructive-foreground shadow-brand hover:bg-[hsl(18_84%_24%)] hover:shadow-brand-md hover:-translate-y-px",
        // Outline — subtle, used for secondary actions
        outline:
          "border border-input bg-card shadow-xs hover:bg-brand-beige-60 hover:text-brand-black hover:border-foreground/20",
        // Secondary — soft beige fill
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-brand-beige-60",
        // Ghost — chromeless, used in toolbars
        ghost:
          "hover:bg-brand-beige-60 hover:text-brand-black",
        // Link — inline text
        link:
          "text-brand-brown underline-offset-4 hover:underline hover:text-brand-orange",
      },
      size: {
        // Mobile-first: 44px (h-11) touch target, smaller on desktop
        default: "h-11 sm:h-10 px-4 py-2",
        sm: "h-9 sm:h-8 rounded-md px-3 text-xs",
        lg: "h-12 sm:h-11 rounded-md px-7 text-base",
        icon: "h-11 w-11 sm:h-9 sm:w-9",
        "icon-sm": "h-9 w-9 sm:h-8 sm:w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
